import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, Plus, Send, FileText, Check, X, Eye, Clock, Search,
  Trash2, Shield, ChevronDown, ChevronUp, RefreshCw, Download
} from 'lucide-react';
import { toast, Toaster } from '../ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  viewed: { label: 'Viewed', color: 'bg-yellow-100 text-yellow-700' },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700' },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700' },
};

const SERVICE_OPTIONS = [
  { id: 'bookkeeping', name: 'Bookkeeping & Accounting', monthly: 25000 },
  { id: 'gst', name: 'GST Compliance', monthly: 15000 },
  { id: 'tds', name: 'TDS Compliance', monthly: 10000 },
  { id: 'incometax', name: 'Income Tax Filing', monthly: 8000 },
  { id: 'mca', name: 'MCA / ROC Filings', monthly: 8000 },
  { id: 'payroll', name: 'Payroll Processing', monthly: 15000 },
  { id: 'postincorp', name: 'Post-Incorporation Compliance', monthly: 12000 },
  { id: 'mis', name: 'MIS & Cash Flow Reports', monthly: 20000 },
  { id: 'audit', name: 'Statutory Audit Support', monthly: 25000 },
  { id: 'cfo', name: 'Virtual CFO Advisory', monthly: 50000 },
  { id: 'fema', name: 'FEMA Compliance', monthly: 20000 },
  { id: 'transfer_pricing', name: 'Transfer Pricing', monthly: 30000 },
];

const INITIAL_FORM = {
  lead_name: '', lead_email: '', lead_company: '',
  selectedServices: [], customItems: [],
  sla_terms: '', payment_terms: 'Net 30',
  contract_duration: '12 months', auto_renew: true, notes: '',
};

const ContractDashboard = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('');
  const [expandedAudit, setExpandedAudit] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const fetchContracts = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contracts`);
      if (res.ok) setContracts((await res.json()).contracts || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const toggleService = (id) => {
    setForm(f => ({
      ...f,
      selectedServices: f.selectedServices.includes(id)
        ? f.selectedServices.filter(s => s !== id)
        : [...f.selectedServices, id]
    }));
  };

  const addCustomItem = () => {
    setForm(f => ({ ...f, customItems: [...f.customItems, { name: '', description: '', amount: 0 }] }));
  };

  const updateCustomItem = (i, key, val) => {
    setForm(f => {
      const items = [...f.customItems];
      items[i] = { ...items[i], [key]: key === 'amount' ? Number(val) || 0 : val };
      return { ...f, customItems: items };
    });
  };

  const removeCustomItem = (i) => {
    setForm(f => ({ ...f, customItems: f.customItems.filter((_, idx) => idx !== i) }));
  };

  const getMonthlyTotal = () => {
    const svcTotal = form.selectedServices.reduce((sum, id) => {
      const svc = SERVICE_OPTIONS.find(s => s.id === id);
      return sum + (svc ? svc.monthly : 0);
    }, 0);
    const customTotal = form.customItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    return svcTotal + customTotal;
  };

  const createContract = async () => {
    if (!form.lead_name || !form.lead_email || (form.selectedServices.length === 0 && form.customItems.filter(i => i.name).length === 0)) {
      toast.error('Fill in client details and select at least one service');
      return;
    }
    const services = [
      ...form.selectedServices.map(id => {
        const svc = SERVICE_OPTIONS.find(s => s.id === id);
        return { service_id: id, name: svc.name, description: '', amount: svc.monthly, is_custom: false };
      }),
      ...form.customItems.filter(i => i.name && i.amount > 0).map(i => ({
        service_id: 'custom', name: i.name, description: i.description, amount: i.amount, is_custom: true,
      })),
    ];

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contracts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_name: form.lead_name, lead_email: form.lead_email, lead_company: form.lead_company,
          services, sla_terms: form.sla_terms, payment_terms: form.payment_terms,
          contract_duration: form.contract_duration, auto_renew: form.auto_renew, notes: form.notes,
        }),
      });
      if (res.ok) {
        toast.success('Contract created!');
        setShowCreate(false);
        setForm(INITIAL_FORM);
        fetchContracts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create contract');
      }
    } catch { toast.error('Network error'); }
  };

  const sendContract = async (contractNumber) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contracts/${contractNumber}/send`, { method: 'POST' });
      if (res.ok) { toast.success('Contract sent to client!'); fetchContracts(); }
      else toast.error('Failed to send');
    } catch { toast.error('Network error'); }
  };

  const deleteContract = async (contractNumber) => {
    if (!window.confirm('Delete this draft contract?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contracts/${contractNumber}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Contract deleted'); fetchContracts(); }
      else { const err = await res.json(); toast.error(err.detail || 'Cannot delete'); }
    } catch { toast.error('Network error'); }
  };

  const downloadPdf = async (contractNumber) => {
    try {
      toast.info('Generating PDF...');
      const res = await fetch(`${BACKEND_URL}/api/admin/contracts/${contractNumber}/download-pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${contractNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else {
        toast.error('PDF generation failed');
      }
    } catch { toast.error('Download failed'); }
  };

  const filtered = contracts.filter(c =>
    !filter || c.leadName?.toLowerCase().includes(filter.toLowerCase()) ||
    c.contractNumber?.toLowerCase().includes(filter.toLowerCase()) ||
    c.leadEmail?.toLowerCase().includes(filter.toLowerCase())
  );

  const stats = {
    total: contracts.length,
    sent: contracts.filter(c => c.status === 'sent').length,
    active: contracts.filter(c => c.status === 'accepted').length,
    pending: contracts.filter(c => ['sent', 'viewed'].includes(c.status)).length,
  };

  const monthlyTotal = getMonthlyTotal();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contract-dashboard">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Contracts & SLAs</h1>
              <p className="text-sm text-gray-500">Create, send, and track client agreements</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-contract-btn">
            <Plus className="w-4 h-4 mr-2" /> New Contract
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: stats.total, icon: FileText },
            { label: 'Sent', value: stats.sent, icon: Send },
            { label: 'Pending', value: stats.pending, icon: Clock },
            { label: 'Active', value: stats.active, icon: Check },
          ].map((s, i) => (
            <Card key={c.token || `contract-${i}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create Contract Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-y-auto" data-testid="create-contract-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mb-10">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">Create New Contract</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}><X className="w-4 h-4" /></Button>
              </div>

              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Client Info */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-3">Client Details</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="Client name *" value={form.lead_name} onChange={e => setForm(f => ({ ...f, lead_name: e.target.value }))} data-testid="contract-client-name" />
                    <Input placeholder="Email *" type="email" value={form.lead_email} onChange={e => setForm(f => ({ ...f, lead_email: e.target.value }))} data-testid="contract-client-email" />
                    <Input placeholder="Company" value={form.lead_company} onChange={e => setForm(f => ({ ...f, lead_company: e.target.value }))} data-testid="contract-client-company" />
                  </div>
                </div>

                {/* Service Selection */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-3">Select Services</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {SERVICE_OPTIONS.map(svc => (
                      <button key={svc.id} data-testid={`contract-service-${svc.id}`}
                        onClick={() => toggleService(svc.id)}
                        className={`text-left p-3 rounded-lg border-2 transition-all ${
                          form.selectedServices.includes(svc.id) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-sm">{svc.name}</span>
                          <span className="text-sm font-bold text-emerald-600">{"\u20B9"}{svc.monthly.toLocaleString()}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Items */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-sm text-gray-700">Custom Line Items</h3>
                    <Button variant="outline" size="sm" onClick={addCustomItem}><Plus className="w-3 h-3 mr-1" /> Add</Button>
                  </div>
                  {form.customItems.map((item, i) => (
                    <div key={s.name || `svc-${i}`} className="flex gap-2 mb-2">
                      <Input placeholder="Service name" className="flex-1" value={item.name} onChange={e => updateCustomItem(i, 'name', e.target.value)} />
                      <Input placeholder="Description" className="flex-1" value={item.description} onChange={e => updateCustomItem(i, 'description', e.target.value)} />
                      <Input type="number" placeholder="Amount" className="w-28" value={item.amount || ''} onChange={e => updateCustomItem(i, 'amount', e.target.value)} />
                      <Button variant="ghost" size="sm" onClick={() => removeCustomItem(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>

                {/* Contract Terms */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-3">Contract Terms</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Duration</label>
                      <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.contract_duration}
                        onChange={e => setForm(f => ({ ...f, contract_duration: e.target.value }))} data-testid="contract-duration">
                        <option value="3 months">3 months</option>
                        <option value="6 months">6 months</option>
                        <option value="12 months">12 months</option>
                        <option value="24 months">24 months</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Payment Terms</label>
                      <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.payment_terms}
                        onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} data-testid="contract-payment-terms">
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 45">Net 45</option>
                        <option value="Due on Receipt">Due on Receipt</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.auto_renew} onChange={e => setForm(f => ({ ...f, auto_renew: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-emerald-600" data-testid="contract-auto-renew" />
                        Auto-renew
                      </label>
                    </div>
                  </div>
                </div>

                {/* SLA & Notes */}
                <Textarea placeholder="SLA Terms (e.g., Response time: 4 business hours, Monthly reconciliation by 5th...)" value={form.sla_terms}
                  onChange={e => setForm(f => ({ ...f, sla_terms: e.target.value }))} rows={3} data-testid="contract-sla-terms" />
                <Textarea placeholder="Internal notes" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} data-testid="contract-notes" />

                {/* Total */}
                <div className="bg-gray-50 rounded-lg p-4 text-sm">
                  <div className="flex justify-between border-b pb-2 mb-2 text-base font-bold">
                    <span>Monthly Total</span>
                    <span className="text-emerald-600" data-testid="contract-monthly-total">{"\u20B9"}{monthlyTotal.toLocaleString()}/mo</span>
                  </div>
                  <p className="text-xs text-gray-400">Duration: {form.contract_duration} | Payment: {form.payment_terms} | Auto-renew: {form.auto_renew ? 'Yes' : 'No'}</p>
                </div>
              </div>

              <div className="p-6 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={createContract} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-contract-btn">
                  <FileText className="w-4 h-4 mr-2" /> Create Contract
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search contracts..." className="pl-9" value={filter} onChange={e => setFilter(e.target.value)} data-testid="contract-search" />
          </div>
        </div>

        {/* Contract List */}
        <div className="space-y-3">
          {loading ? (
            <p className="text-center text-gray-500 py-12">Loading contracts...</p>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No contracts yet. Create your first one!</p>
            </CardContent></Card>
          ) : filtered.map(c => {
            const st = STATUS_MAP[c.status] || STATUS_MAP.draft;
            const isAuditOpen = expandedAudit === c.contractNumber;
            return (
              <Card key={c.contractNumber} className="hover:shadow-md transition-shadow" data-testid={`contract-row-${c.contractNumber}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{c.contractNumber}</span>
                          <Badge className={st.color}>{st.label}</Badge>
                          {c.autoRenew && <Badge className="bg-purple-100 text-purple-700">Auto-renew</Badge>}
                        </div>
                        <p className="text-sm text-gray-600">{c.leadName} {c.leadCompany ? `- ${c.leadCompany}` : ''}</p>
                        <p className="text-xs text-gray-400">{c.leadEmail} | {c.contractDuration} | {c.paymentTerms}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{"\u20B9"}{c.monthlyTotal?.toLocaleString()}/mo</p>
                        <p className="text-xs text-gray-400">{c.services?.length || 0} services</p>
                      </div>
                      <div className="flex gap-2">
                        {c.status === 'draft' && (
                          <>
                            <Button size="sm" onClick={() => sendContract(c.contractNumber)} className="bg-blue-600 hover:bg-blue-700" data-testid={`send-${c.contractNumber}`}>
                              <Send className="w-3.5 h-3.5 mr-1" /> Send
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteContract(c.contractNumber)} data-testid={`delete-${c.contractNumber}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => window.open(`/contract/${c.token}`, '_blank')} data-testid={`preview-${c.contractNumber}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(c.contractNumber)}
                          className="text-emerald-600 border-emerald-300 hover:bg-emerald-50" data-testid={`download-pdf-${c.contractNumber}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setExpandedAudit(isAuditOpen ? null : c.contractNumber)} data-testid={`audit-toggle-${c.contractNumber}`}>
                          {isAuditOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Audit Trail (expandable) */}
                  {isAuditOpen && c.auditTrail && (
                    <div className="mt-4 pt-4 border-t" data-testid={`audit-trail-${c.contractNumber}`}>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Audit Trail</h4>
                      <div className="space-y-2">
                        {c.auditTrail.map((entry, i) => (
                          <div key={`revision-${i}`} className="flex items-start gap-3 text-xs">
                            <div className={`w-2 h-2 mt-1 rounded-full flex-shrink-0 ${
                              entry.action === 'accepted' ? 'bg-green-500' : entry.action === 'declined' ? 'bg-red-500' : 'bg-blue-500'
                            }`} />
                            <div>
                              <span className="font-medium capitalize">{entry.action}</span>
                              <span className="text-gray-400 ml-2">{new Date(entry.timestamp).toLocaleString()}</span>
                              {entry.ip && <span className="text-gray-400 ml-2">IP: {entry.ip}</span>}
                              {entry.details && <p className="text-gray-500 mt-0.5">{entry.details}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ContractDashboard;
